import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import axios from "axios";
import { Link, useHistory } from "react-router-dom";
import {
  FaUsers,
  FaCog,
  FaUserPlus,
  FaCalculator,
  FaCalendarAlt,
  FaCreditCard,
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaFileExport,
  FaPlus,
  FaMoneyBillWave,
  FaArrowUp,
  FaArrowDown,
  FaArrowRight,
  FaHome,
  FaFileInvoiceDollar,
} from "react-icons/fa";
import SessionManager from "../Utils/SessionManager";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const fontStack =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/** Page-local design tokens (mirrors PayRollAddStaff) */
const ui = {
  pageBg: "#f5f7fb",
  surface: "#ffffff",
  surfaceAlt: "#f8fafc",
  border: "rgba(148, 163, 184, 0.32)",
  divider: "rgba(148, 163, 184, 0.22)",
  muted: "#64748b",
  text: "#0f172a",
  textSoft: "#334155",
  ring: "rgba(3, 169, 244, 0.18)",
  danger: "#ef4444",
  success: "#10b981",
  warn: "#f59e0b",
  info: "#0ea5e9",
  radiusXl: 20,
  radiusLg: 16,
  radiusMd: 12,
  shadowSoft: "0 1px 2px rgba(15, 23, 42, 0.04), 0 2px 8px rgba(15, 23, 42, 0.04)",
  shadowCard: "0 1px 2px rgba(15, 23, 42, 0.04), 0 6px 24px rgba(15, 23, 42, 0.05)",
  shadowLift: "0 1px 2px rgba(15, 23, 42, 0.05), 0 16px 48px rgba(15, 23, 42, 0.10)",
  heroGrad: (c) =>
    `linear-gradient(120deg, ${c.primary_color_shade9} 0%, ${c.primary_color_shade7} 45%, ${c.primary_color_shade5} 100%)`,
};

const STATUS_PALETTE = {
  Paid:       { bg: "rgba(16, 185, 129, 0.14)", color: "#047857", dot: "#10b981" },
  Pending:    { bg: "rgba(245, 158, 11, 0.16)", color: "#b45309", dot: "#f59e0b" },
  Processing: { bg: "rgba(14, 165, 233, 0.14)", color: "#0369a1", dot: "#0ea5e9" },
  default:    { bg: "rgba(148, 163, 184, 0.18)", color: "#334155", dot: "#94a3b8" },
};

function statusPalette(status) {
  return STATUS_PALETTE[status] || STATUS_PALETTE.default;
}

function formatINR(value) {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numeric);
}

function formatDateDMY(value) {
  if (!value) return "—";
  const parts = String(value).split("-");
  if (parts.length === 3) {
    const [y, m, d] = parts;
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(value);
  if (!Number.isNaN(dt.getTime())) return dt.toLocaleDateString();
  return String(value);
}

const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatYearMonth(value) {
  if (!value) return "—";
  const parts = String(value).split("-");
  if (parts.length >= 2) {
    const y = parts[0];
    const m = parseInt(parts[1], 10);
    if (m >= 1 && m <= 12) return `${MONTH_SHORT[m - 1]} ${y}`;
  }
  return String(value);
}

/** Compact list attendance: Present, Absent, Leave, Late (matches payroll API fields). */
const ATTENDANCE_COMPACT = [
  {
    code: "P",
    key: "staff_present",
    label: "Present",
    bg: "rgba(240, 253, 244, 0.98)",
    border: "rgba(34, 197, 94, 0.45)",
    color: "#15803d",
  },
  {
    code: "A",
    key: "staff_absent",
    label: "Absent",
    bg: "rgba(254, 242, 242, 0.98)",
    border: "rgba(239, 68, 68, 0.45)",
    color: "#b91c1c",
  },
  {
    code: "L",
    key: "staff_leave",
    label: "Leave",
    bg: "rgba(255, 247, 237, 0.98)",
    border: "rgba(234, 88, 12, 0.45)",
    color: "#c2410c",
  },
  {
    code: "LT",
    key: "late_day",
    label: "Late",
    bg: "rgba(255, 241, 242, 0.98)",
    border: "rgba(225, 29, 72, 0.45)",
    color: "#be123c",
  },
];

function formatAttendanceCount(value) {
  if (value === null || value === undefined || value === "") return "0";
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  if (Number.isInteger(n)) return String(n);
  const fixed = n.toFixed(1);
  return fixed.endsWith(".0") ? String(Math.round(n)) : fixed;
}

function getPreviousYearMonth(yearMonth) {
  if (!yearMonth || !/^\d{4}-\d{2}$/.test(String(yearMonth))) return null;
  const [y, m] = String(yearMonth).split("-").map((v) => parseInt(v, 10));
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Derive dashboard summary from getPayRollMonthlySalary rows (selected month). */
function computePayrollSummaryFromStaffList(staffList) {
  let monthTotal = 0;
  let totalPaid = 0;
  let totalBalance = 0;
  let processedPayments = 0;
  let pendingPayments = 0;

  for (const s of staffList) {
    monthTotal += Number(s.net_salary) || 0;
    totalPaid += Number(s.total_paid) || 0;
    totalBalance += Number(s.balance_due) || 0;
    const status = String(s.status || "").trim();
    if (status === "Paid") {
      processedPayments += 1;
    } else if (status === "Pending" || status === "Processing") {
      pendingPayments += 1;
    }
  }

  return {
    totalStaff: staffList.length,
    monthTotal,
    totalPaid,
    totalBalance,
    processedPayments,
    pendingPayments,
  };
}

function growthPercent(current, previous) {
  const cur = Number(current) || 0;
  const prev = Number(previous) || 0;
  if (prev <= 0) return cur > 0 ? 100 : 0;
  return Math.round(((cur - prev) / prev) * 1000) / 10;
}

const AttendanceCompactCell = React.memo(function AttendanceCompactCell({ record }) {
  return (
    <td style={{ minWidth: 132 }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          alignItems: "center",
        }}
      >
        {ATTENDANCE_COMPACT.map(({ code, key, label, bg, border, color }) => (
          <span
            key={code}
            title={`${label}: ${formatAttendanceCount(record[key])}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              padding: "2px 6px",
              borderRadius: 6,
              fontSize: 10,
              fontWeight: 800,
              fontVariantNumeric: "tabular-nums",
              background: bg,
              border: `1px solid ${border}`,
              color,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ letterSpacing: 0.02 }}>{code}</span>
            <span>{formatAttendanceCount(record[key])}</span>
          </span>
        ))}
      </div>
    </td>
  );
});

/* Decorative blobs (constants — never re-rendered). */
const HERO_ORB_TR = (
  <span
    aria-hidden
    style={{
      position: "absolute",
      right: -60,
      top: -60,
      width: 220,
      height: 220,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.08)",
      filter: "blur(2px)",
    }}
  />
);
const HERO_ORB_BL = (
  <span
    aria-hidden
    style={{
      position: "absolute",
      left: -40,
      bottom: -80,
      width: 180,
      height: 180,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.06)",
    }}
  />
);
const STAFF_HEADER_ORB = (
  <span
    aria-hidden
    style={{
      position: "absolute",
      right: -40,
      top: -40,
      width: 160,
      height: 160,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.07)",
    }}
  />
);

function scrollToStaffList() {
  const el = document.getElementById("pp-staff-list");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

//Created By Cursor
function MetricCard({ title, value, icon, gradient, onView, viewLabel }) {
  return (
    <div className="metric-card lift" style={{ background: gradient }}>
      <div className="metric-card__content">
        <div className="metric-card__label">{title}</div>
        <div className="metric-card__value">{value}</div>
      </div>
      <span className="metric-icon" aria-hidden>
        {icon}
      </span>
      <button
        type="button"
        className="metric-card__view"
        onClick={onView}
        aria-label={viewLabel || `View ${title}`}
        title={viewLabel || `View ${title}`}
      >
        <FaArrowRight aria-hidden />
      </button>
    </div>
  );
}

function summaryCellToneClass(tone) {
  if (tone === "success") return "pp-summary-cell--success";
  if (tone === "danger") return "pp-summary-cell--danger";
  if (tone === "warn") return "pp-summary-cell--warn";
  if (tone === "growth-up") return "pp-summary-cell--growth-up pp-summary-cell--success";
  if (tone === "growth-down") return "pp-summary-cell--growth-down pp-summary-cell--danger";
  return "pp-summary-cell--muted";
}

const PayrollSummaryPanels = React.memo(function PayrollSummaryPanels({
  payrollSummary,
  selectedMonth,
  growth,
  growthPositive,
}) {
  const paid = payrollSummary.processedPayments || 0;
  const pending = payrollSummary.pendingPayments || 0;
  const totalStatus = paid + pending;
  const paidPct = totalStatus > 0 ? Math.round((paid / totalStatus) * 100) : 0;
  const pendingPct = totalStatus > 0 ? 100 - paidPct : 0;
  const monthLabel = selectedMonth ? formatYearMonth(selectedMonth) : null;

  return (
    <div className="pp-summary-section pp-summary-section--compact">
      <div className="row g-2">
        <div className="col-lg-7">
          <div className="pp-summary-card">
            <div className="pp-summary-card__head pp-summary-card__head--inline">
              <span className="pp-summary-card__icon" aria-hidden>
                <FaCalendarAlt />
              </span>
              <div className="pp-summary-card__head-text min-w-0">
                <h3 className="pp-summary-card__title">
                  Monthly summary
                  {monthLabel ? (
                    <span className="pp-summary-card__title-muted">
                      {" "}
                      · {monthLabel}
                    </span>
                  ) : null}
                </h3>
              </div>
              <div className="pp-summary-card__head-metric">
                <span className="pp-summary-card__head-metric-label">Net</span>
                <span className="pp-summary-card__head-metric-value">
                  {formatINR(payrollSummary.monthTotal)}
                </span>
              </div>
            </div>
            <div className="pp-summary-card__body">
              <div className="pp-summary-grid">
                {[
                  {
                    key: "paid",
                    tone: "success",
                    label: "Paid out",
                    value: formatINR(payrollSummary.totalPaid),
                  },
                  {
                    key: "balance",
                    tone: "danger",
                    label: "Balance due",
                    value: formatINR(payrollSummary.totalBalance),
                  },
                  {
                    key: "prev",
                    tone: "muted",
                    label: "Prev. month",
                    value: formatINR(payrollSummary.previousMonth),
                  },
                  {
                    key: "growth",
                    tone: growthPositive ? "growth-up" : "growth-down",
                    label: "Growth",
                    value: `${growthPositive ? "+" : ""}${growth}%`,
                  },
                ].map((cell) => (
                  <div
                    key={cell.key}
                    className={`${summaryCellToneClass(cell.tone)} pp-summary-cell`}
                  >
                    <span className="pp-summary-cell__label">{cell.label}</span>
                    <span className="pp-summary-cell__value">{cell.value}</span>
                    <button
                      type="button"
                      className="pp-summary-cell__view"
                      onClick={scrollToStaffList}
                      aria-label={`View ${cell.label}`}
                      title={`View ${cell.label}`}
                    >
                      <FaArrowRight aria-hidden />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="pp-summary-card pp-summary-card--status">
            <div className="pp-summary-card__head pp-summary-card__head--inline">
              <span className="pp-summary-card__icon" aria-hidden>
                <FaCreditCard />
              </span>
              <div className="pp-summary-card__head-text min-w-0">
                <h3 className="pp-summary-card__title">Payment status</h3>
              </div>
            </div>
            <div className="pp-summary-card__body">
              {totalStatus > 0 ? (
                <div className="pp-summary-progress">
                  <div className="pp-summary-progress__track" role="presentation">
                    {paidPct > 0 ? (
                      <div
                        className="pp-summary-progress__paid"
                        style={{ width: `${paidPct}%` }}
                      />
                    ) : null}
                    {pendingPct > 0 ? (
                      <div
                        className="pp-summary-progress__pending"
                        style={{ width: `${pendingPct}%` }}
                      />
                    ) : null}
                  </div>
                  <div className="pp-summary-progress__caption">
                    {paidPct}% paid
                    {pending > 0 ? ` · ${pending} pending` : ""}
                  </div>
                </div>
              ) : (
                <p className="pp-summary-empty">No payroll rows yet</p>
              )}
              <div
                className="pp-summary-inline-stats pp-summary-inline-stats--below"
                aria-label="Payment counts"
              >
                <span className="pp-summary-inline-stats__item pp-summary-inline-stats__item--paid">
                  <span className="pp-summary-inline-stats__n">{paid}</span>
                  <span className="pp-summary-inline-stats__l">Paid</span>
                </span>
                <span className="pp-summary-inline-stats__item pp-summary-inline-stats__item--pending">
                  <span className="pp-summary-inline-stats__n">{pending}</span>
                  <span className="pp-summary-inline-stats__l">Pending</span>
                </span>
                <span className="pp-summary-inline-stats__item pp-summary-inline-stats__item--staff">
                  <span className="pp-summary-inline-stats__n">
                    {payrollSummary.totalStaff || 0}
                  </span>
                  <span className="pp-summary-inline-stats__l">Staff</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

function SkeletonRow({ cols = 11 }) {
  return (
    <tr>
      <td colSpan={cols} style={{ padding: 0 }}>
        <div className="d-flex align-items-center gap-2 py-2 px-1">
          <div className="pp-skeleton" style={{ width: 36, height: 36, borderRadius: "50%" }} />
          <div className="flex-grow-1">
            <div className="pp-skeleton mb-1" style={{ width: "40%", height: 12 }} />
            <div className="pp-skeleton" style={{ width: "25%", height: 10 }} />
          </div>
          <div className="pp-skeleton d-none d-md-block" style={{ width: 60, height: 10 }} />
          <div className="pp-skeleton" style={{ width: 70, height: 10 }} />
          <div className="pp-skeleton" style={{ width: 60, height: 14, borderRadius: 999 }} />
        </div>
      </td>
    </tr>
  );
}

const StaffPayrollRow = React.memo(function StaffPayrollRow({ s, onDetails }) {
  const st = statusPalette(s.status);
  return (
    <tr>
      <td>
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: COLORS.primary_color_shade5,
              color: "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: 14,
              flexShrink: 0,
              boxShadow: "0 2px 6px rgba(3, 169, 244, 0.32)",
            }}
            aria-hidden
          >
            {(s.staff_name || "?").charAt(0).toUpperCase()}
          </div>
          <span
            className="fw-bold text-truncate min-w-0"
            style={{ color: ui.text, fontSize: 14 }}
          >
            {s.staff_name}
          </span>
        </div>
      </td>
      <AttendanceCompactCell record={s} />
      <td
        style={{
          color: ui.text,
          fontVariantNumeric: "tabular-nums",
          fontWeight: 600,
        }}
      >
        {formatINR(s.basic_salary)}
      </td>
      <td
        className="d-none d-lg-table-cell"
        style={{
          color: ui.success,
          fontVariantNumeric: "tabular-nums",
          fontWeight: 600,
        }}
      >
        {formatINR(s.allowances)}
      </td>
      <td
        className="d-none d-lg-table-cell"
        style={{
          color: ui.danger,
          fontVariantNumeric: "tabular-nums",
          fontWeight: 600,
        }}
      >
        {formatINR(s.deductions)}
      </td>
      <td
        className="text-end fw-bold"
        style={{
          color: ui.text,
          fontSize: 15,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatINR(s.net_salary)}
      </td>
      <td
        className="text-end d-none d-md-table-cell"
        style={{
          color: ui.success,
          fontVariantNumeric: "tabular-nums",
          fontWeight: 700,
        }}
      >
        {formatINR(s.total_paid)}
      </td>
      <td
        className="text-end d-none d-md-table-cell"
        style={{
          color: ui.danger,
          fontVariantNumeric: "tabular-nums",
          fontWeight: 700,
        }}
      >
        {formatINR(s.balance_due)}
      </td>
      <td>
        <span
          className="badge border-0 d-inline-flex align-items-center gap-1"
          style={{
            background: st.bg,
            color: st.color,
            fontWeight: 700,
            fontSize: 11,
            padding: "5px 10px",
            borderRadius: 999,
            letterSpacing: "0.02em",
          }}
        >
          <span
            aria-hidden
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: st.dot,
            }}
          />
          {s.status || "—"}
        </span>
      </td>
      <td>
        <button
          type="button"
          className="btn btn-sm fw-semibold border-0"
          onClick={() => onDetails?.(s)}
          style={{
            background: "rgba(3, 169, 244, 0.12)",
            color: COLORS.primary_color_shade8,
            borderRadius: 8,
            padding: "6px 14px",
            fontSize: 12,
          }}
        >
          Details
        </button>
      </td>
    </tr>
  );
});

const PayRoll = () => {
  const history = useHistory();
  const [loggedIn] = useState(localStorage.getItem("token") !== null);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [mSessionList, setMSessionList] = useState([]);
  const [staffPayrollList, setStaffPayrollList] = useState([]);
  const [previousMonthTotal, setPreviousMonthTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const listReqIdRef = useRef(0);
  const prevMonthReqIdRef = useRef(0);
  const sessionReqIdRef = useRef(0);

  /* On unmount, invalidate any in-flight fetch so it can't update state. */
  useEffect(() => {
    return () => {
      listReqIdRef.current = -1;
      prevMonthReqIdRef.current = -1;
      sessionReqIdRef.current = -1;
    };
  }, []);

  const fetchPreviousMonthTotal = useCallback(async () => {
    const prevYm = getPreviousYearMonth(selectedMonth);
    if (!prevYm || !selectedSessionId) {
      setPreviousMonthTotal(0);
      return;
    }
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getPayRollMonthlySalary.php`;
    const reqId = ++prevMonthReqIdRef.current;
    const [year, month] = prevYm.split("-");

    try {
      const response = await axios.get(URL, {
        params: {
          school_code: SCHOOL_CODE,
          session_id: selectedSessionId,
          month: parseInt(month, 10),
          year: parseInt(year, 10),
        },
      });
      if (reqId !== prevMonthReqIdRef.current) return;
      if (response.data?.api_status === "OK") {
        const rows = response.data.staff_payroll || [];
        const sum = rows.reduce(
          (acc, row) => acc + (Number(row.net_salary) || 0),
          0
        );
        setPreviousMonthTotal(sum);
      } else {
        setPreviousMonthTotal(0);
      }
    } catch (error) {
      if (reqId !== prevMonthReqIdRef.current) return;
      console.error("Error fetching previous month payroll:", error);
      setPreviousMonthTotal(0);
    }
  }, [selectedMonth, selectedSessionId]);

  //Created By Cursor
  const fetchStaffPayrollList = useCallback(async () => {
    if (!selectedMonth || !selectedSessionId) return;
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const BASE = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;
    const reqId = ++listReqIdRef.current;

    try {
      const [year, month] = selectedMonth.split("-");
      const monthParams = {
        school_code: SCHOOL_CODE,
        session_id: selectedSessionId,
        month: parseInt(month, 10),
        year: parseInt(year, 10),
      };
      const staffParams = {
        school_code: SCHOOL_CODE,
        session_id: selectedSessionId,
      };

      // Staff master list from tm_payroll_staff_details; monthly rows enrich salary/attendance.
      const [staffRes, monthlyRes] = await Promise.all([
        axios.get(`${BASE}getPayRollStaffList.php`, { params: staffParams }),
        axios.get(`${BASE}getPayRollMonthlySalary.php`, { params: monthParams }),
      ]);

      if (reqId !== listReqIdRef.current) return;

      const payrollStaff =
        staffRes.data?.api_status === "OK" && Array.isArray(staffRes.data.data)
          ? staffRes.data.data
          : [];

      const monthlyRows =
        monthlyRes.data?.api_status === "OK" &&
        Array.isArray(monthlyRes.data.staff_payroll)
          ? monthlyRes.data.staff_payroll
          : [];

      const monthlyByStaffId = {};
      for (const row of monthlyRows) {
        const id = row?.staff_id != null ? String(row.staff_id) : "";
        if (id) monthlyByStaffId[id] = row;
      }

      // Base list = Active payroll staff in tm_payroll_staff_details for session.
      const merged = payrollStaff.map((staff) => {
        const id = staff?.staff_id != null ? String(staff.staff_id) : "";
        const monthRow = id ? monthlyByStaffId[id] : null;
        return {
          ...staff,
          // getPayRollStaffList aliases basic_salary as `salary`
          basic_salary:
            monthRow?.basic_salary ?? staff.basic_salary ?? staff.salary ?? "",
          designation: staff.staff_role || monthRow?.designation || "",
          ...(monthRow || {
            staff_present: 0,
            staff_absent: 0,
            staff_leave: 0,
            late_day: 0,
            allowances: 0,
            deductions: 0,
            net_salary: 0,
            total_paid: 0,
            balance_due: 0,
            status: "Pending",
            year_month: selectedMonth,
            session_id: selectedSessionId,
          }),
        };
      });

      setStaffPayrollList(merged);
    } catch (error) {
      if (reqId !== listReqIdRef.current) return;
      console.error("Error fetching payroll staff list:", error);
      setStaffPayrollList([]);
    }
  }, [selectedMonth, selectedSessionId]);

  const fetchSessionList = useCallback(async () => {
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const FUNCTION_NAME = "getSessionList.php";
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;
    const reqId = ++sessionReqIdRef.current;

    try {
      const response = await axios.get(URL, { params: { school_code: SCHOOL_CODE } });
      if (reqId !== sessionReqIdRef.current) return;
      if (!response.data || response.data.api_status !== "OK") {
        if (response.data?.api_remark) {
          console.error("Error fetching session list:", response.data.api_remark);
        }
        return;
      }
      const sessions = response.data.data || [];
      setMSessionList(sessions);
      if (sessions.length === 0) return;

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;

      const chosen =
        sessions.find(
          (s) =>
            s.status === "Active" ||
            (s.session_name && s.session_name.includes(String(currentYear)))
        ) || sessions[0];

      setSelectedSessionId(chosen.session_id);

      if (chosen.start_date && chosen.end_date) {
        const startDate = new Date(chosen.start_date);
        const endDate = new Date(chosen.end_date);
        if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return;

        const sessionStartYear = startDate.getFullYear();
        const sessionEndYear = endDate.getFullYear();
        const sessionStartMonth = startDate.getMonth() + 1;
        const sessionEndMonth = endDate.getMonth() + 1;

        const inRange =
          sessionStartYear <= currentYear &&
          sessionEndYear >= currentYear &&
          currentMonth >= sessionStartMonth &&
          currentMonth <= sessionEndMonth;

        if (inRange) {
          setSelectedMonth(`${currentYear}-${String(currentMonth).padStart(2, "0")}`);
        } else {
          setSelectedMonth(
            `${sessionStartYear}-${String(sessionStartMonth).padStart(2, "0")}`
          );
        }
      }
    } catch (error) {
      if (reqId !== sessionReqIdRef.current) return;
      console.error("Error fetching session list:", error);
    }
  }, []);

  useEffect(() => {
    fetchSessionList();
  }, [fetchSessionList]);

  /* Load staff list + previous month total for summary growth. */
  useEffect(() => {
    if (!selectedMonth || !selectedSessionId) return;
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      fetchStaffPayrollList(),
      fetchPreviousMonthTotal(),
    ]).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [
    selectedMonth,
    selectedSessionId,
    fetchStaffPayrollList,
    fetchPreviousMonthTotal,
  ]);

  const payrollSummary = useMemo(() => {
    const base = computePayrollSummaryFromStaffList(staffPayrollList);
    return {
      ...base,
      previousMonth: previousMonthTotal,
      growth: growthPercent(base.monthTotal, previousMonthTotal),
    };
  }, [staffPayrollList, previousMonthTotal]);

  const monthOptions = useMemo(() => {
    if (!selectedSessionId || mSessionList.length === 0) return [];
    const selectedSession = mSessionList.find(
      (s) => s.session_id === selectedSessionId
    );
    if (!selectedSession?.start_date || !selectedSession?.end_date) return [];
    const startDate = new Date(selectedSession.start_date);
    const endDate = new Date(selectedSession.end_date);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      return [];
    }
    const months = [];
    const cur = new Date(startDate);
    while (cur <= endDate) {
      const monthName = cur.toLocaleString("default", { month: "long" });
      const year = cur.getFullYear();
      const monthValue = cur.getMonth();
      const monthKey = `${year}-${String(monthValue + 1).padStart(2, "0")}`;
      months.push({ value: monthKey, label: `${monthName} ${year}`, key: monthKey });
      cur.setMonth(cur.getMonth() + 1);
    }
    return months;
  }, [selectedSessionId, mSessionList]);

  const filteredStaffList = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return staffPayrollList;
    return staffPayrollList.filter((s) => {
      const hay = [
        s.staff_id,
        s.staff_name,
        s.staff_role,
        s.designation,
        s.department_name,
      ]
        .map((v) => (v ?? "").toString().toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [staffPayrollList, searchTerm]);

  const isFiltered = Boolean(searchTerm.trim());

  const handleSessionChange = useCallback(
    (e) => {
      const id = e.target.value;
      const sess = mSessionList.find((s) => s.session_id === id);
      if (!sess) return;
      setSelectedSessionId(id);
      if (sess.start_date) {
        const startDate = new Date(sess.start_date);
        if (!Number.isNaN(startDate.getTime())) {
          const year = startDate.getFullYear();
          const month = String(startDate.getMonth() + 1).padStart(2, "0");
          setSelectedMonth(`${year}-${month}`);
        }
      }
    },
    [mSessionList]
  );

  const handleMonthChange = useCallback((e) => {
    setSelectedMonth(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => setSearchTerm(""), []);

  const openMonthlyPayrollDetails = useCallback(
    (row) => {
      const salaryKey = row?.salary_id || row?.id;
      if (!salaryKey) return;
      const qs = new URLSearchParams();
      if (row.session_id != null) qs.set("session_id", String(row.session_id));
      if (row.year_month) qs.set("year_month", String(row.year_month));
      const search = qs.toString();
      history.push({
        pathname: `/AdminMain/PayRollSalaryDetails/${encodeURIComponent(String(salaryKey))}`,
        search: search ? `?${search}` : "",
        state: { staffRecord: row },
      });
    },
    [history]
  );

  const handleRefresh = useCallback(() => {
    if (!selectedMonth || !selectedSessionId) return;
    setLoading(true);
    Promise.allSettled([
      fetchStaffPayrollList(),
      fetchPreviousMonthTotal(),
    ]).finally(() => setLoading(false));
  }, [
    selectedMonth,
    selectedSessionId,
    fetchStaffPayrollList,
    fetchPreviousMonthTotal,
  ]);

  const growth = Number(payrollSummary.growth ?? 0);
  const growthPositive = growth >= 0;

  if (!loggedIn) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <h2>Access Denied</h2>
            <p>You must be logged in to view this page.</p>
            <Link to="/Login" className="btn btn-primary">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="content payroll-page"
      style={{
        background: ui.pageBg,
        fontFamily: fontStack,
        color: ui.text,
        minHeight: "100%",
      }}
    >
      {/* Breadcrumbs */}
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ borderBottom: `1px solid ${ui.divider}`, paddingBottom: 6 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <Link
            to="/AdminMain/PayRoll"
            aria-label="Payroll home"
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(3, 169, 244, 0.12)",
              color: COLORS.primary_color_shade6,
              textDecoration: "none",
            }}
          >
            <FaHome />
          </Link>
          <Link
            to="/AdminMain/PayRollSettings"
            className="d-inline-flex align-items-center gap-1"
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: ui.textSoft,
              textDecoration: "none",
            }}
          >
            <FaCog style={{ fontSize: 11 }} />
            Salary Settings
          </Link>
        </div>
        <div className="rightside-content-header d-flex align-items-center">
          <BackButton />
        </div>
      </div>

      <div
        className="bg_page_white_rounded box_shadow animated fadeInUp"
        style={{
          "--mMargin": "10px",
          background: "transparent",
          boxShadow: "none",
        }}
      >
        {/* Dashboard card */}
        <div
          style={{
            background: ui.surface,
            borderRadius: ui.radiusXl,
            border: `1px solid ${ui.border}`,
            boxShadow: ui.shadowLift,
            overflow: "hidden",
            maxWidth: 1180,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {/* Hero header */}
          <div
            style={{
              background: ui.heroGrad(COLORS),
              padding: "16px 18px",
              borderBottom: "1px solid rgba(255,255,255,0.12)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {HERO_ORB_TR}
            {HERO_ORB_BL}

            <div
              className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center justify-content-between gap-3"
              style={{ position: "relative" }}
            >
              <div className="d-flex align-items-center gap-3 text-white flex-shrink-0 min-w-0">
                <span
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 14,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.14)",
                    border: "1px solid rgba(255,255,255,0.28)",
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <FaCalculator style={{ fontSize: 16 }} />
                </span>
                <div className="min-w-0">
                  <div style={{ fontWeight: 800, fontSize: 17, letterSpacing: "0.01em" }}>
                    Payroll Dashboard
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                    Monitor payouts, track payments & manage monthly payroll
                  </div>
                </div>
              </div>

              <div className="d-flex flex-wrap align-items-center gap-2 flex-shrink-0">
                <select
                  value={selectedSessionId}
                  onChange={handleSessionChange}
                  className="pp-pill-select pp-pill-select--session"
                  aria-label="Choose academic session"
                >
                  {mSessionList.length === 0 ? (
                    <option value="">Loading sessions…</option>
                  ) : (
                    mSessionList.map((s) => (
                      <option key={s.session_id} value={s.session_id}>
                        {s.session_name}
                        {s.status ? ` (${s.status})` : ""}
                      </option>
                    ))
                  )}
                </select>
                <select
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  className="pp-pill-select"
                  disabled={monthOptions.length === 0}
                  aria-label="Choose month"
                  style={{ minWidth: 150 }}
                >
                  {monthOptions.length > 0 ? (
                    monthOptions.map((m) => (
                      <option key={m.key} value={m.value}>
                        {m.label}
                      </option>
                    ))
                  ) : (
                    <option value="">No months</option>
                  )}
                </select>
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={loading || !selectedSessionId}
                  className={`pp-icon-btn ${loading ? "spinning" : ""}`}
                  title="Refresh"
                  aria-label="Refresh"
                >
                  <FaSyncAlt aria-hidden />
                </button>
                <Link
                  to="/AdminMain/PayRollAddStaff"
                  className="btn lift payroll-cta d-inline-flex align-items-center gap-1"
                  style={{
                    borderRadius: 999,
                    fontWeight: 700,
                    padding: "8px 16px",
                    fontSize: 13,
                  }}
                >
                  <FaUserPlus /> Add Staff
                </Link>
              </div>
            </div>
          </div>

          {/* Metrics */}
          <div className="row g-3 p-3" style={{ margin: 0 }}>
            <div className="col-md-3 col-sm-6">
              <MetricCard
                title="Total Staff"
                value={payrollSummary.totalStaff || 0}
                icon={<FaUsers />}
                gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                viewLabel="View payroll staff list"
                onView={() => {
                  const qs = selectedSessionId
                    ? `?session_id=${encodeURIComponent(String(selectedSessionId))}`
                    : "";
                  history.push(`/AdminMain/PayRollStaffList${qs}`);
                }}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <MetricCard
                title="Month Total"
                value={formatINR(payrollSummary.monthTotal)}
                icon={<FaMoneyBillWave />}
                gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                onView={scrollToStaffList}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <MetricCard
                title="Processed"
                value={payrollSummary.processedPayments || 0}
                icon={<FaFileInvoiceDollar />}
                gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
                onView={scrollToStaffList}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <MetricCard
                title="Growth"
                value={`${growthPositive ? "+" : ""}${growth}%`}
                icon={growthPositive ? <FaArrowUp /> : <FaArrowDown />}
                gradient={
                  growthPositive
                    ? "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
                    : "linear-gradient(135deg, #ff6a88 0%, #ff99ac 100%)"
                }
                onView={scrollToStaffList}
              />
            </div>
          </div>

          <div className="px-3 pb-1 pt-0">
            <PayrollSummaryPanels
              payrollSummary={payrollSummary}
              selectedMonth={selectedMonth}
              growth={growth}
              growthPositive={growthPositive}
            />
          </div>
        </div>

        {/* Staff payroll list */}
        <div
          id="pp-staff-list"
          style={{
            background: ui.surface,
            border: `1px solid ${ui.border}`,
            borderRadius: ui.radiusLg,
            boxShadow: ui.shadowSoft,
            overflow: "hidden",
            maxWidth: 1180,
            marginLeft: "auto",
            marginRight: "auto",
            marginTop: 16,
            marginBottom: 16,
          }}
        >
          {/* List header */}
          <div
            style={{
              background: `linear-gradient(135deg, ${COLORS.primary_color_shade8} 0%, ${COLORS.primary_color_shade6} 100%)`,
              color: "#fff",
              padding: "14px 18px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {STAFF_HEADER_ORB}
            <div
              className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-2"
              style={{ position: "relative" }}
            >
              <div className="d-flex align-items-center gap-3 min-w-0">
                <span
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.14)",
                    border: "1px solid rgba(255,255,255,0.28)",
                    backdropFilter: "blur(6px)",
                    flexShrink: 0,
                  }}
                >
                  <FaUsers style={{ fontSize: 15 }} />
                </span>
                <div className="min-w-0">
                  <div style={{ fontWeight: 800, fontSize: 15 }}>
                    Staff Payroll Details
                    {selectedMonth
                      ? ` (${formatYearMonth(selectedMonth)})`
                      : ""}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      opacity: 0.85,
                      marginTop: 1,
                      fontWeight: 600,
                    }}
                  >
                    {loading
                      ? "Loading…"
                      : isFiltered
                        ? `${filteredStaffList.length} of ${staffPayrollList.length} shown`
                        : `${staffPayrollList.length} total`}
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <div className="pp-search-wrap">
                  <FaSearch className="pp-search-icon" aria-hidden />
                  <input
                    type="text"
                    className="pp-search"
                    placeholder="Search name, ID, role…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    aria-label="Search staff"
                  />
                  {searchTerm ? (
                    <button
                      type="button"
                      className="pp-search-clear"
                      onClick={handleClearSearch}
                      aria-label="Clear search"
                      title="Clear"
                    >
                      <FaTimes />
                    </button>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="btn border-0 d-inline-flex align-items-center gap-1"
                  style={{
                    background: "rgba(255,255,255,0.18)",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 12,
                    padding: "6px 12px",
                    borderRadius: 999,
                    backdropFilter: "blur(6px)",
                  }}
                  title="Export"
                >
                  <FaFileExport /> Export
                </button>
                <Link
                  to="/AdminMain/PayRollAddSalary"
                  className="btn lift payroll-cta d-inline-flex align-items-center gap-1"
                  style={{
                    borderRadius: 999,
                    fontWeight: 700,
                    padding: "7px 14px",
                    fontSize: 13,
                  }}
                >
                  <FaPlus /> Add Salary
                </Link>
              </div>
            </div>
          </div>

          {/* List body */}
          <div className="px-2 py-2">
            {loading ? (
              <div className="table-responsive">
                <table className="table align-middle mb-0 pp-table">
                  <tbody>
                    {[0, 1, 2, 3].map((i) => (
                      <SkeletonRow key={i} />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : staffPayrollList.length === 0 ? (
              <div className="text-center py-5" style={{ color: ui.muted, fontSize: 14 }}>
                <div
                  className="mx-auto mb-2 d-inline-flex align-items-center justify-content-center"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 18,
                    background: "rgba(148, 163, 184, 0.18)",
                    color: ui.muted,
                  }}
                >
                  <FaFileInvoiceDollar style={{ fontSize: 22 }} />
                </div>
                <div style={{ fontWeight: 800, color: ui.text, fontSize: 15 }}>
                  No payroll staff found
                </div>
                <div style={{ fontSize: 13 }}>
                  Add staff via Payroll → Add Staff for this session.
                </div>
              </div>
            ) : filteredStaffList.length === 0 ? (
              <div className="text-center py-5" style={{ color: ui.muted, fontSize: 14 }}>
                <div
                  className="mx-auto mb-2 d-inline-flex align-items-center justify-content-center"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 18,
                    background: "rgba(148, 163, 184, 0.18)",
                    color: ui.muted,
                  }}
                >
                  <FaSearch style={{ fontSize: 20 }} />
                </div>
                <div style={{ fontWeight: 800, color: ui.text, fontSize: 15 }}>
                  No matching staff
                </div>
                <div style={{ fontSize: 13 }}>Try a different search term.</div>
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="btn mt-2"
                  style={{
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: 13,
                    padding: "6px 14px",
                    border: `1px solid ${ui.border}`,
                    background: ui.surface,
                    color: ui.textSoft,
                  }}
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle mb-0 pp-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: 0.04,
                          whiteSpace: "nowrap",
                        }}
                        title="Present · Absent · Leave · Late"
                      >
                        Attendance
                      </th>
                      <th>Basic</th>
                      <th className="d-none d-lg-table-cell">Allowances</th>
                      <th className="d-none d-lg-table-cell">Deductions</th>
                      <th className="text-end">Net Salary</th>
                      <th className="text-end d-none d-md-table-cell">Paid Amount</th>
                      <th className="text-end d-none d-md-table-cell">Balance Due</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStaffList.map((s, idx) => (
                      <StaffPayrollRow
                        key={`${s.staff_id}-${idx}`}
                        s={s}
                        onDetails={openMonthlyPayrollDetails}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default PayRoll;
